import { inject } from '@angular/core'
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, UrlSerializer, CanActivateFn, CanActivateChildFn } from '@angular/router'
import { AuthService } from '@app/modules/auth/auth.service'
import { merge, of, Observable } from 'rxjs'
import { filter, first, switchMap } from 'rxjs/operators'
import { FeatureFlagService } from '@core/services/feature-flag.service'
import { CLIENT_LANDING_URL } from '@core/constants/team-type'

export const noAuthGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> => {
  const authService = inject(AuthService)
  const urlSerializer = inject(UrlSerializer)
  const featureFlagService = inject(FeatureFlagService)
  const isLoggedIn$ = authService.isLoggedIn$

  const isNotLoggedIn = (isLoggedIn) => !isLoggedIn
  const canActivateRoute = (isLoggedIn) => of(!isLoggedIn)

  const canActivate$ = isLoggedIn$.pipe(filter(isNotLoggedIn), switchMap(canActivateRoute))

  const currentUser$ = isLoggedIn$.pipe(
    filter(Boolean),
    switchMap(() => authService.currentUser$),
  )

  const adminRedirect$ = currentUser$.pipe(
    filter((user) => user.isAdmin()),
    switchMap(() => of(urlSerializer.parse('/admin'))),
  )

  // With the v2 client UI enabled, send an already-logged-in client straight to the v2 landing
  // instead of '/client'. The '/client' hop mounts the v1 shell (global sidebar in app.component)
  // for a frame before ClientRedirectComponent bounces to /v2/client/map, which flashed the v1 icon
  // set before the v2 rail (seen on impersonation, which enters via '/' then /login). Admin/reseller
  // have no v2 yet, so their redirects are unchanged.
  const clientLandingUrl = featureFlagService.uiV2 ? `/${CLIENT_LANDING_URL}` : '/client'
  const clientRedirect$ = currentUser$.pipe(
    filter((user) => user.isClient()),
    switchMap(() => of(urlSerializer.parse(clientLandingUrl))),
  )

  const resellerRedirect$ = currentUser$.pipe(
    filter((user) => user.isReseller()),
    switchMap(() => of(urlSerializer.parse('/reseller'))),
  )

  return merge(canActivate$, adminRedirect$, clientRedirect$, resellerRedirect$).pipe(first())
}

export const noAuthChildGuard: CanActivateChildFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  return noAuthGuard(route, state)
}
